import type {
  FileIngestionErrors,
  FileIngestionResults,
  FileProcessingStatus,
  FileProcessingStatusState,
  SignedUrlResponse,
  UploadSession,
} from '@ouster/webviz-react-components';

export const RESUME_AUTO_THRESHOLD_MS = 15 * 60 * 1000;
export const RESUMABLE_EXPIRY_SKEW_BUFFER_MS = 30 * 1000;

const INTERRUPTED_UPLOAD_ERROR_CODE = 'UPLOAD_INTERRUPTED';
const RESUMABLE_CHUNK_SIZE_BYTES = 64 * 1024 * 1024;

/**
 * Backoff schedule for transient resumable-upload failures.
 *
 * Corporate networks and VPN adapters can take 5-30s to re-establish routing,
 * DNS and proxy state after the browser reports `online`. The browser never
 * surfaces the underlying error code (ERR_NAME_NOT_RESOLVED, ERR_NETWORK_CHANGED
 * and friends all arrive as an XHR `error` event with status 0), so every
 * network-layer failure is treated as transient and retried on this schedule.
 * Retrying is safe because the resumable status query is idempotent and
 * re-reports the authoritative committed offset before any bytes are resent.
 */
export const RESUMABLE_RETRY_DELAYS_MS = [2000, 4000, 8000, 16000, 30000];

export const RESUMABLE_UPLOAD_LOG_PREFIX = '[resumable-upload]';

export const logResumableUpload = (
  message: string,
  detail?: Record<string, unknown>
) => {
  console.info(`${RESUMABLE_UPLOAD_LOG_PREFIX} ${message}`, detail ?? {});
};

/**
 * Backoff sleep that wakes early when the browser reports the network is back.
 *
 * A plain timer would keep sleeping through the reconnect: a drop that lands on
 * the 30s step leaves the upload idle for the rest of that step even though the
 * link recovered seconds later. Waking on `online` keeps the long tail of the
 * schedule available for genuinely long outages without paying for it once the
 * connection actually returns.
 */
const defaultWait = (delayMs: number) =>
  new Promise<void>((resolve) => {
    let isSettled = false;

    const settle = (wokeOnReconnect: boolean) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      clearTimeout(timer);

      if (typeof window !== 'undefined') {
        window.removeEventListener('online', wakeOnReconnect);
      }

      if (wokeOnReconnect) {
        logResumableUpload('backoff woken early by online event', { delayMs });
      }

      resolve();
    };

    const wakeOnReconnect = () => settle(true);
    const timer = setTimeout(() => settle(false), delayMs);

    if (typeof window !== 'undefined') {
      window.addEventListener('online', wakeOnReconnect);
    }
  });

type CompletedUploadLike = {
  datasetUploadState?: string;
  drive_id?: number;
  completeTimeStamp?: number;
  dataFile: {
    name: string;
  };
};

type UploadErrorLike = {
  message: string;
};

type FileProcessingStatusLike = {
  state?: FileProcessingStatusState;
  ingestion_result?: FileIngestionResults;
  errors?: FileIngestionErrors;
  resumable_expired?: boolean;
};

type ResponseLike = {
  ok: boolean;
  status: number;
  statusText: string;
};

type UploadSessionValidationStatus = {
  state?: string;
};

type HttpStatusError = Error & {
  cause?: unknown;
  status?: number;
};

const getHttpStatus = (error: unknown) => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
};

type UploadTagLike = {
  name?: string | null;
};

type UploadDataCategoryLike = {
  name?: string | null;
};

type UploadDataFileLike = {
  name: string;
};

type UploadDriveEventDataSetLike = {
  tags?: UploadTagLike[] | null;
  dataCategory?: UploadDataCategoryLike | null;
  dataFile: UploadDataFileLike;
};

export type UploadDriveCreatedSensorInfo = {
  sdk_prod_sn: string;
  sdk_prod_line: string;
  sdk_prod_type: string;
  sdk_prod_generation: string;
  sdk_prod_hw_rev: string;
  sdk_prod_beam_spacing: string;
  sdk_prod_resolution: string;
  sdk_fw_version_core: string;
  sdk_lidar_mode: string;
  sdk_fw_branch: string;
  sdk_fw_build_rev: string;
  sdk_fw_image_rev: string;
};

export type UploadDriveCreatedDetail = {
  driveId: number;
  orgId: number;
  tags: string[];
  dataCategory: string | null;
  dataFile: string;
  uploadedBy: string | null;
  processedAt: string;
  sensorInfo: UploadDriveCreatedSensorInfo[];
};

type UploadSessionResponseBody = {
  uuid: string;
  source_bucket?: string;
};

type SignedUrlResponseBody = {
  signed_url: string;
  uuid: string;
  resumable_url?: string;
  resumable_expires_at?: string;
};

type SignedUrlUploadTarget = {
  signed_url: string;
  upload_id: string;
  session_id: string;
  resumable_url?: string;
  resumable_expires_at?: string;
};

type UploadRequestHandleLike = {
  abort: () => void;
};

export type NetworkRecoveryDetail = {
  interruptionDurationMs: number;
};

type UploadRequestResultLike = {
  request: UploadRequestHandleLike;
  promise: Promise<void>;
};

type UploadProgressEventLike = {
  loaded: number;
  total: number;
};

type XhrEventTargetLike = {
  addEventListener: (
    eventName: 'progress',
    listener: (event: UploadProgressEventLike) => void
  ) => void;
};

type XhrLike = {
  status: number;
  upload?: XhrEventTargetLike;
  addEventListener: (
    eventName: 'load' | 'error' | 'abort',
    listener: () => void
  ) => void;
  open: (method: string, url: string) => void;
  setRequestHeader: (name: string, value: string) => void;
  send: (body?: Blob | File | null) => void;
  abort: () => void;
  getResponseHeader?: (name: string) => string | null;
  getAllResponseHeaders?: () => string;
};

type InterruptedUploadLike = {
  configFile?: File;
  configUploadProgress: number;
  configUploadTarget?: SignedUrlUploadTarget;
  dataFile: {
    name: string;
    size?: number;
  };
  dataUploadProgress: number;
  dataUploadTarget?: SignedUrlUploadTarget;
  interruptedAt?: number;
};

type ResumeCheckFileStatus = Pick<
  FileProcessingStatus,
  'file_id' | 'file_type' | 'resumable_expired' | 'session_id'
>;

export const createInterruptedUploadError = (message: string) => {
  const error = new Error(message) as Error & {
    code: string;
    interruptedAt: number;
  };

  error.code = INTERRUPTED_UPLOAD_ERROR_CODE;
  error.interruptedAt = Date.now();

  return error;
};

export const isTransientRequestError = (error: unknown): boolean => {
  const requestError = error as HttpStatusError | null;
  const status = getHttpStatus(error);

  return (
    error instanceof TypeError ||
    status === 408 ||
    status === 429 ||
    (typeof status === 'number' && status >= 500 && status <= 599) ||
    (requestError?.cause !== undefined &&
      isTransientRequestError(requestError.cause))
  );
};

const parsePersistedOffset = (rangeHeader: string | null) => {
  if (!rangeHeader) {
    return 0;
  }

  const match = rangeHeader.match(/(\d+)\s*-\s*(\d+)/);

  if (!match) {
    return 0;
  }

  return Number(match[2]) + 1;
};

const getExposedResponseHeader = <T extends XhrLike>(
  xhr: T,
  headerName: string
) => {
  const allResponseHeaders = xhr.getAllResponseHeaders?.();

  if (!allResponseHeaders) {
    return null;
  }

  const normalizedHeaderName = headerName.toLowerCase();
  const headerLine = allResponseHeaders
    .split(/\r?\n/)
    .find((line) => line.toLowerCase().startsWith(`${normalizedHeaderName}:`));

  if (!headerLine) {
    return null;
  }

  return headerLine.slice(headerLine.indexOf(':') + 1).trim() || null;
};

const isCompleteUploadStatus = (status: number) =>
  status === 200 || status === 201;

// Matches the transient set in isTransientRequestError. GCS returns 429/503 under
// load and 500/502/504 through proxies and load balancers; all are safe to retry
// because the status query re-reports the committed offset first.
const isRetryableResumableStatus = (status: number) =>
  status === 408 ||
  status === 429 ||
  status === 500 ||
  status === 502 ||
  status === 503 ||
  status === 504;

const createUploadStatusError = (status: number) => {
  const error = new Error(
    `Upload failed with status ${status}`
  ) as HttpStatusError;
  error.status = status;

  return error;
};

const getUploadFailureStatus = (error: unknown) => {
  const status = getHttpStatus(error);

  if (status !== undefined) {
    return status;
  }

  const message = error instanceof Error ? error.message : String(error);
  const match = message.match(/Upload failed with status (\d+)/);

  return match ? Number(match[1]) : undefined;
};

export const isInterruptedUploadError = (error: unknown) =>
  (error as { code?: unknown } | null)?.code === INTERRUPTED_UPLOAD_ERROR_CODE;

/**
 * A resumable request is worth retrying when it failed at the network layer
 * (XHR `error` event -> interrupted upload error, no status available) or when
 * the server returned a transient status. Cancellations are never retryable.
 */
export const isRetryableResumableError = (error: unknown) => {
  if (isInterruptedUploadError(error)) {
    return true;
  }

  const status = getUploadFailureStatus(error);

  return status !== undefined && isRetryableResumableStatus(status);
};

const runResumableOffsetRequest = <T extends XhrLike>({
  createXhr,
  file,
  resumableUrl,
  setActiveRequest,
}: {
  createXhr: () => T;
  file: File;
  resumableUrl: string;
  setActiveRequest: (request: T | null) => void;
}) =>
  new Promise<number>((resolve, reject) => {
    const xhr = createXhr();
    setActiveRequest(xhr);

    xhr.addEventListener('load', () => {
      setActiveRequest(null);

      if (xhr.status === 308) {
        const rangeHeader = getExposedResponseHeader(xhr, 'Range');

        if (!rangeHeader) {
          // GCS omits Range when nothing is committed yet, but a proxy that
          // strips Access-Control-Expose-Headers looks identical from here and
          // silently restarts the file from byte 0. Surface it either way.
          logResumableUpload('status query returned 308 with no Range header', {
            fileName: file.name,
            fileSize: file.size,
          });
        }

        resolve(parsePersistedOffset(rangeHeader));
        return;
      }

      if (isCompleteUploadStatus(xhr.status)) {
        resolve(file.size);
        return;
      }

      reject(createUploadStatusError(xhr.status));
    });

    xhr.addEventListener('error', () => {
      setActiveRequest(null);
      reject(
        createInterruptedUploadError('Upload failed due to network error')
      );
    });

    xhr.addEventListener('abort', () => {
      setActiveRequest(null);
      reject(new Error('Upload cancelled'));
    });

    xhr.open('PUT', resumableUrl);
    xhr.setRequestHeader('Content-Range', `bytes */${file.size}`);
    xhr.send(null);
  });

const runResumableChunkUpload = <T extends XhrLike>({
  createXhr,
  file,
  onProgress,
  resumableUrl,
  setActiveRequest,
  startOffset,
}: {
  createXhr: () => T;
  file: File;
  onProgress: (progress: number) => void;
  resumableUrl: string;
  setActiveRequest: (request: T | null) => void;
  startOffset: number;
}) =>
  new Promise<number>((resolve, reject) => {
    const endExclusive = Math.min(
      startOffset + RESUMABLE_CHUNK_SIZE_BYTES,
      file.size
    );
    const endInclusive = endExclusive - 1;
    const chunk = file.slice(startOffset, endExclusive);
    const xhr = createXhr();
    setActiveRequest(xhr);

    xhr.upload?.addEventListener('progress', (event) => {
      const progress = Math.min(
        ((startOffset + event.loaded) / file.size) * 100,
        100
      );
      onProgress(progress);
    });

    xhr.addEventListener('load', () => {
      setActiveRequest(null);

      if (xhr.status === 308) {
        resolve(parsePersistedOffset(getExposedResponseHeader(xhr, 'Range')));
        return;
      }

      if (isCompleteUploadStatus(xhr.status)) {
        onProgress(100);
        resolve(file.size);
        return;
      }

      reject(createUploadStatusError(xhr.status));
    });

    xhr.addEventListener('error', () => {
      setActiveRequest(null);
      reject(
        createInterruptedUploadError('Upload failed due to network error')
      );
    });

    xhr.addEventListener('abort', () => {
      setActiveRequest(null);
      reject(new Error('Upload cancelled'));
    });

    xhr.open('PUT', resumableUrl);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader(
      'Content-Range',
      `bytes ${startOffset}-${endInclusive}/${file.size}`
    );
    xhr.send(chunk);
  });

const createResumableFileUpload = <T extends XhrLike>({
  createXhr,
  file,
  onProgress,
  resumableUrl,
  onNetworkRecovered,
  retryDelaysMs = RESUMABLE_RETRY_DELAYS_MS,
  wait = defaultWait,
}: {
  createXhr: () => T;
  file: File;
  onProgress: (progress: number) => void;
  onNetworkRecovered?: (detail: NetworkRecoveryDetail) => void;
  resumableUrl: string;
  retryDelaysMs?: number[];
  wait?: (delayMs: number) => Promise<void>;
}): UploadRequestResultLike => {
  let activeRequest: T | null = null;
  let isCancelled = false;
  const request: UploadRequestHandleLike = {
    abort: () => {
      // Also stops the loop if we are sitting in a backoff delay rather than in
      // an in-flight request, where there is nothing to abort.
      isCancelled = true;
      activeRequest?.abort();
    },
  };

  const setActiveRequest = (nextRequest: T | null) => {
    activeRequest = nextRequest;
  };

  const throwIfCancelled = () => {
    if (isCancelled) {
      throw new Error('Upload cancelled');
    }
  };

  /**
   * Start of the current unbroken run of network failures.
   *
   * createInterruptedUploadError stamps interruptedAt when it is constructed, so
   * the error that finally escapes after a full backoff run would otherwise be
   * stamped up to a minute after the network actually dropped. The uploader
   * reads that timestamp to decide how long the disruption lasted, and therefore
   * whether to auto-resume silently, show the resumed toast, or ask for
   * confirmation - so the retries must not swallow the real interruption time.
   * Cleared only when a chunk actually commits bytes, so an earlier recovered
   * blip does not inflate the duration of a later one. A status query that
   * succeeds on its own does not count: on a flaky link the small query often
   * gets through while the large chunk PUT still fails, and treating that as
   * recovery would keep resetting the clock.
   */
  let interruptionStartedAt: number | null = null;

  const noteUploadProgressed = () => {
    if (interruptionStartedAt === null) {
      return;
    }

    // Bytes committed after a failure run: the upload recovered on its own,
    // without ever reaching the interrupted state the resume flow reacts to.
    // Nothing else can report this, so it is the only signal available when the
    // browser emits no offline/online pair - the VPN case this all exists for.
    const interruptionDurationMs = Math.max(
      Date.now() - interruptionStartedAt,
      0
    );
    interruptionStartedAt = null;

    logResumableUpload('upload recovered without being interrupted', {
      fileName: file.name,
      interruptionDurationMs,
    });
    onNetworkRecovered?.({ interruptionDurationMs });
  };

  const noteRequestFailed = (error: unknown) => {
    if (!isInterruptedUploadError(error)) {
      return;
    }

    const stampedAt = (error as { interruptedAt?: number }).interruptedAt;
    interruptionStartedAt =
      interruptionStartedAt ??
      (typeof stampedAt === 'number' ? stampedAt : Date.now());
  };

  const withInterruptionStart = (error: unknown) => {
    if (isInterruptedUploadError(error) && interruptionStartedAt !== null) {
      (error as { interruptedAt?: number }).interruptedAt =
        interruptionStartedAt;
    }

    return error;
  };

  const backoff = async (attempt: number, label: string, error: unknown) => {
    const delayMs = retryDelaysMs[attempt];
    logResumableUpload(`${label} failed, retrying after backoff`, {
      attempt: attempt + 1,
      maxAttempts: retryDelaysMs.length,
      delayMs,
      fileName: file.name,
      status: getUploadFailureStatus(error),
      isNetworkError: isInterruptedUploadError(error),
      navigatorOnLine:
        typeof navigator === 'undefined' ? undefined : navigator.onLine,
      message: error instanceof Error ? error.message : String(error),
    });

    await wait(delayMs);
    throwIfCancelled();
  };

  const runWithRetry = async (
    requestFactory: () => Promise<number>,
    label: string
  ) => {
    let attempt = 0;

    while (true) {
      try {
        return await requestFactory();
      } catch (error) {
        noteRequestFailed(error);

        if (
          !isRetryableResumableError(error) ||
          attempt >= retryDelaysMs.length
        ) {
          logResumableUpload(`${label} failed, giving up`, {
            attempts: attempt + 1,
            fileName: file.name,
            status: getUploadFailureStatus(error),
            isNetworkError: isInterruptedUploadError(error),
            navigatorOnLine:
              typeof navigator === 'undefined' ? undefined : navigator.onLine,
            message: error instanceof Error ? error.message : String(error),
          });
          throw withInterruptionStart(error);
        }

        await backoff(attempt, label, error);
        attempt += 1;
      }
    }
  };

  const promise: Promise<void> = (async () => {
    const queryCommittedOffset = () =>
      runWithRetry(
        () =>
          runResumableOffsetRequest({
            createXhr,
            file,
            resumableUrl,
            setActiveRequest,
          }),
        'resumable status query'
      );

    // Recovery budget per offset rather than a single attempt: one VPN
    // renegotiation can fail the same chunk several times in a row.
    const chunkRecoveryAttempts = new Map<number, number>();
    let nextOffset = await queryCommittedOffset();

    if (nextOffset >= file.size) {
      onProgress(100);
      return;
    }

    while (nextOffset < file.size) {
      throwIfCancelled();
      const startOffset = nextOffset;

      try {
        nextOffset = await runResumableChunkUpload({
          createXhr,
          file,
          onProgress,
          resumableUrl,
          setActiveRequest,
          startOffset,
        });

        if (nextOffset > startOffset) {
          noteUploadProgressed();
        }
      } catch (error) {
        noteRequestFailed(error);
        const attempt = chunkRecoveryAttempts.get(startOffset) ?? 0;

        if (
          !isRetryableResumableError(error) ||
          attempt >= retryDelaysMs.length
        ) {
          logResumableUpload('chunk upload failed, giving up', {
            startOffset,
            attempts: attempt + 1,
            fileName: file.name,
            status: getUploadFailureStatus(error),
            isNetworkError: isInterruptedUploadError(error),
            navigatorOnLine:
              typeof navigator === 'undefined' ? undefined : navigator.onLine,
            message: error instanceof Error ? error.message : String(error),
          });
          throw withInterruptionStart(error);
        }

        chunkRecoveryAttempts.set(startOffset, attempt + 1);
        await backoff(attempt, `chunk upload at offset ${startOffset}`, error);
        // Never assume how much of the failed chunk landed; ask GCS.
        nextOffset = await queryCommittedOffset();
      }
    }
  })();

  return { request, promise };
};

export function getInterruptedUploadNames<T extends InterruptedUploadLike>(
  uploads: T[]
) {
  return uploads.map((upload) => upload.dataFile.name);
}

export function getInterruptedDurationMs<T extends InterruptedUploadLike>(
  uploads: T[],
  nowMs = Date.now()
) {
  const interruptedAtValues = uploads
    .map((upload) => upload.interruptedAt)
    .filter((value): value is number => typeof value === 'number');

  if (interruptedAtValues.length === 0) {
    return 0;
  }

  return Math.max(nowMs - Math.min(...interruptedAtValues), 0);
}

export function getResumeStatusChecks<
  T extends InterruptedUploadLike & {
    uploadSessionId?: string;
    dataUploadId?: string;
  }
>(uploads: T[]): ResumeCheckFileStatus[] {
  return uploads.flatMap((upload) => {
    const checks: ResumeCheckFileStatus[] = [];

    if (
      upload.uploadSessionId &&
      upload.dataUploadId &&
      upload.dataUploadProgress < 100
    ) {
      checks.push({
        file_id: upload.dataUploadId,
        file_type: 'data',
        session_id: upload.uploadSessionId,
      });
    }

    if (
      upload.uploadSessionId &&
      upload.configUploadTarget?.upload_id &&
      upload.configUploadProgress < 100
    ) {
      checks.push({
        file_id: upload.configUploadTarget.upload_id,
        file_type: 'config',
        session_id: upload.uploadSessionId,
      });
    }

    return checks;
  });
}

/**
 * Resolve the resume status for every interrupted file, retrying the whole
 * batch on transient failures.
 *
 * This is the first request fired after a reconnect, so on a corporate network
 * or VPN it lands in the window where routing, DNS and proxy state are still
 * settling. The browser reports those as `TypeError: Failed to fetch` with no
 * distinguishable cause, which isTransientRequestError already treats as
 * transient. Without this retry a single failure here abandons the resume until
 * the next offline -> online transition, which on a VPN may never arrive.
 */
export async function fetchResumeStatusesWithRetry({
  checks,
  pollFileUploadStatus,
  retryDelaysMs = RESUMABLE_RETRY_DELAYS_MS,
  wait = defaultWait,
}: {
  checks: ResumeCheckFileStatus[];
  pollFileUploadStatus: (
    sessionId: string,
    fileId: string,
    fileType?: 'data' | 'config'
  ) => Promise<FileProcessingStatus>;
  retryDelaysMs?: number[];
  wait?: (delayMs: number) => Promise<void>;
}): Promise<FileProcessingStatus[]> {
  let attempt = 0;

  while (true) {
    try {
      return await Promise.all(
        checks.map((statusCheck) =>
          pollFileUploadStatus(
            statusCheck.session_id,
            statusCheck.file_id,
            statusCheck.file_type
          )
        )
      );
    } catch (error) {
      if (!isTransientRequestError(error) || attempt >= retryDelaysMs.length) {
        logResumableUpload('resume status check failed, giving up', {
          attempts: attempt + 1,
          checkCount: checks.length,
          navigatorOnLine:
            typeof navigator === 'undefined' ? undefined : navigator.onLine,
          message: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }

      const delayMs = retryDelaysMs[attempt];
      logResumableUpload('resume status check failed, retrying after backoff', {
        attempt: attempt + 1,
        maxAttempts: retryDelaysMs.length,
        delayMs,
        checkCount: checks.length,
        navigatorOnLine:
          typeof navigator === 'undefined' ? undefined : navigator.onLine,
        message: error instanceof Error ? error.message : String(error),
      });

      await wait(delayMs);
      attempt += 1;
    }
  }
}

export function canResumeInterruptedUploads<T extends InterruptedUploadLike>({
  estimatedRemainingSeconds,
  nowMs = Date.now(),
  uploads,
}: {
  estimatedRemainingSeconds: number;
  nowMs?: number;
  uploads: T[];
}) {
  const incompleteExpiryTimes = uploads.flatMap((upload) => {
    const expiryTimes: number[] = [];

    if (
      upload.dataUploadProgress < 100 &&
      upload.dataUploadTarget?.resumable_expires_at
    ) {
      expiryTimes.push(
        Date.parse(upload.dataUploadTarget.resumable_expires_at)
      );
    }

    if (
      upload.configUploadProgress < 100 &&
      upload.configUploadTarget?.resumable_expires_at
    ) {
      expiryTimes.push(
        Date.parse(upload.configUploadTarget.resumable_expires_at)
      );
    }

    return expiryTimes.filter((value) => Number.isFinite(value));
  });

  if (incompleteExpiryTimes.length === 0) {
    return true;
  }

  const earliestExpiryMs = Math.min(...incompleteExpiryTimes);
  const estimatedFinishMs =
    nowMs + estimatedRemainingSeconds * 1000 + RESUMABLE_EXPIRY_SKEW_BUFFER_MS;

  return earliestExpiryMs > estimatedFinishMs;
}

export function getCompletedRecordingDatasets<T extends CompletedUploadLike>(
  completed: T[]
): T[] {
  return completed
    .filter(
      (dataset) =>
        dataset.datasetUploadState === 'submitted' &&
        typeof dataset.drive_id === 'number'
    )
    .slice()
    .sort((a, b) => {
      if (!a.completeTimeStamp && !b.completeTimeStamp) return 0;
      if (!a.completeTimeStamp) return 1;
      if (!b.completeTimeStamp) return -1;
      return b.completeTimeStamp - a.completeTimeStamp;
    });
}

export function buildRecordingLinksClipboardText<T extends CompletedUploadLike>(
  completed: T[],
  orgId: number,
  origin: string
): string {
  return completed
    .map((dataset) => {
      const path = `/organization/${orgId}/drive/${dataset.drive_id}`;
      const link = `${origin}${path}`;
      return `- ${dataset.dataFile.name}\n  ${link}`;
    })
    .join('\n');
}

export function buildRecordingLinksCopiedDescription(count: number): string {
  return `Copied ${count} recording link${count > 1 ? 's' : ''} to clipboard.`;
}

export function buildCreatedUploadSessionDetail(
  responseBody: UploadSessionResponseBody
): {
  sessionId: string;
  sourceBucket?: string;
  uploadSession: UploadSession;
} {
  return {
    sessionId: responseBody.uuid,
    sourceBucket: responseBody.source_bucket,
    uploadSession: {
      session_id: responseBody.uuid,
      status: 'created',
    },
  };
}

export function buildSignedUrlUploadResponse(
  responseBody: SignedUrlResponseBody,
  sessionId: string
): SignedUrlResponse {
  const uploadResponse: SignedUrlUploadTarget = {
    signed_url: responseBody.signed_url,
    upload_id: responseBody.uuid,
    session_id: sessionId,
    resumable_url: responseBody.resumable_url,
    resumable_expires_at: responseBody.resumable_expires_at,
  };

  return uploadResponse;
}

export function createSignedUrlFileUpload<T extends XhrLike>({
  file,
  uploadTarget,
  onProgress,
  onNetworkRecovered,
  createXhr,
  retryDelaysMs,
  wait,
}: {
  file: File;
  uploadTarget: SignedUrlUploadTarget;
  onProgress: (progress: number) => void;
  onNetworkRecovered?: (detail: NetworkRecoveryDetail) => void;
  createXhr: () => T;
  retryDelaysMs?: number[];
  wait?: (delayMs: number) => Promise<void>;
}): UploadRequestResultLike {
  if (uploadTarget.resumable_url) {
    return createResumableFileUpload({
      createXhr,
      file,
      onProgress,
      onNetworkRecovered,
      resumableUrl: uploadTarget.resumable_url,
      retryDelaysMs,
      wait,
    });
  }

  const xhr = createXhr();
  const request: UploadRequestHandleLike = {
    abort: () => {
      xhr.abort();
    },
  };

  const promise: Promise<void> = new Promise<void>((resolve, reject) => {
    xhr.upload?.addEventListener('progress', (event) => {
      const progress = Math.min((event.loaded / event.total) * 100, 100);
      onProgress(progress);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('PUT', uploadTarget.signed_url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.send(file);
  });

  return { request, promise };
}

export function resolveUploadOrgId(
  sessionOrgMap: Map<string, number>,
  sessionId?: string,
  fallbackOrgId?: number | null
): number {
  const orgId = sessionId
    ? sessionOrgMap.get(sessionId) ?? fallbackOrgId
    : fallbackOrgId;

  if (!orgId) {
    throw new Error('No organization found');
  }

  return orgId;
}

export function normalizeFileUploadStatus(
  statusBody: FileProcessingStatusLike,
  fileUploadId: string,
  sessionId: string,
  fileType: 'data' | 'config' | undefined,
  getParsedErrors: (
    errors: FileIngestionErrors
  ) => [UploadErrorLike[], UploadErrorLike[]]
): FileProcessingStatus {
  const [ingestionWarnings, fatalErrors] = statusBody.errors
    ? getParsedErrors(statusBody.errors)
    : [[], []];

  const warnings = ingestionWarnings.map((warning) => warning.message);
  const errors = fatalErrors.map((error) => error.message);

  return {
    file_id: fileUploadId,
    session_id: sessionId,
    state: statusBody.state || 'waiting',
    ingestion_result: statusBody.ingestion_result,
    warnings: warnings.length > 0 ? warnings : undefined,
    errors: errors.length > 0 ? errors : undefined,
    file_type: fileType,
    resumable_expired: statusBody.resumable_expired,
  };
}

export async function closeUploadSessionWithRetry({
  closeSession,
  retryDelays,
  wait,
  onRetry,
}: {
  closeSession: () => Promise<ResponseLike>;
  retryDelays: number[];
  wait: (ms: number) => Promise<void>;
  onRetry: (attempt: number, delayMs: number) => void;
}): Promise<ResponseLike> {
  let lastError: Error | null = null;
  let closeResponse: ResponseLike | null = null;

  for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
    try {
      closeResponse = await closeSession();

      if (closeResponse.ok) {
        return closeResponse;
      }

      const responseError = new Error(
        `Server returned ${closeResponse.status}: ${closeResponse.statusText}`
      ) as HttpStatusError;
      responseError.status = closeResponse.status;
      throw responseError;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt === retryDelays.length) {
        break;
      }

      onRetry(attempt + 1, retryDelays[attempt]);
      await wait(retryDelays[attempt]);
    }
  }

  const closeError = new Error(
    `Failed to close upload session after ${
      retryDelays.length + 1
    } attempts. Last error: ${lastError?.message}`
  ) as HttpStatusError;
  closeError.cause = lastError;
  closeError.status = getHttpStatus(lastError);
  throw closeError;
}

export async function pollUploadSessionValidation({
  sessionId,
  orgId,
  fetchSessionStatus,
  wait,
  pollIntervalMs = 1000,
}: {
  sessionId: string;
  orgId: string;
  fetchSessionStatus: (
    sessionId: string,
    orgId: string
  ) => Promise<UploadSessionValidationStatus>;
  wait: (ms: number) => Promise<void>;
  pollIntervalMs?: number;
}): Promise<void> {
  for (;;) {
    let validationData: UploadSessionValidationStatus;

    try {
      validationData = await fetchSessionStatus(sessionId, orgId);
    } catch (error) {
      if (!isTransientRequestError(error)) {
        throw error;
      }

      await wait(pollIntervalMs);
      continue;
    }

    if (
      validationData.state === 'moving' ||
      validationData.state === 'closed'
    ) {
      return;
    }

    if (
      validationData.state === 'failed' ||
      validationData.state === 'validation_failed'
    ) {
      throw new Error('Dataset validation failed during final processing');
    }

    await wait(pollIntervalMs);
  }
}

export async function cleanupUploadDriveOnError({
  driveId,
  orgId,
  deleteDrive,
  clearDriveId,
  onCleanupError,
}: {
  driveId: string;
  orgId: string;
  deleteDrive: (driveId: string, orgId: string) => Promise<void>;
  clearDriveId: () => void;
  onCleanupError: (
    cleanupError: unknown,
    detail: { driveId: string; orgId: string }
  ) => void;
}): Promise<void> {
  try {
    await deleteDrive(driveId, orgId);
    clearDriveId();
  } catch (cleanupError) {
    onCleanupError(cleanupError, { driveId, orgId });
  }
}

export function buildUploadDriveCreatedDetail({
  driveId,
  orgId,
  uploadDataSet,
  uploadedBy,
  processedAt,
  configInfo,
}: {
  driveId: number;
  orgId: number;
  uploadDataSet: UploadDriveEventDataSetLike;
  uploadedBy?: string | null;
  processedAt: string | Date;
  configInfo?: FileIngestionResults[] | null;
}): UploadDriveCreatedDetail {
  return {
    driveId,
    orgId,
    tags: (uploadDataSet.tags || []).flatMap((tag) =>
      tag.name ? [tag.name] : []
    ),
    dataCategory: uploadDataSet.dataCategory?.name ?? null,
    dataFile: uploadDataSet.dataFile.name,
    uploadedBy: uploadedBy ?? null,
    processedAt: formatUploadProcessedAt(processedAt),
    sensorInfo: buildUploadDriveCreatedSensorInfo(configInfo),
  };
}

export function formatUploadProcessedAt(processedAt: string | Date): string {
  const isoString =
    typeof processedAt === 'string' ? processedAt : processedAt.toISOString();

  return isoString.replace('Z', '').replace(/(\.\d{3})$/, '$1000');
}

export function buildUploadDriveCreatedSensorInfo(
  configInfo?: FileIngestionResults[] | null
): UploadDriveCreatedSensorInfo[] {
  return (configInfo || []).map((sensor) => ({
    sdk_prod_sn: sensor.prod_sn ?? '',
    sdk_prod_line: sensor.prod_line ?? '',
    sdk_prod_type: sensor.prod_type ?? '',
    sdk_prod_generation: sensor.prod_generation ?? '',
    sdk_prod_hw_rev: sensor.prod_hw_rev ?? '',
    sdk_prod_beam_spacing: sensor.prod_beam_spacing ?? '',
    sdk_prod_resolution: sensor.prod_resolution ?? '',
    sdk_fw_version_core: sensor.fw_version_core ?? '',
    sdk_lidar_mode: sensor.lidar_mode ?? '',
    sdk_fw_branch: sensor.fw_branch ?? '',
    sdk_fw_build_rev: sensor.build_rev ?? '',
    sdk_fw_image_rev: sensor.image_rev ?? '',
  }));
}
